import React, { useEffect, useMemo, useState } from "react";
import { useChartData } from "./quantStore";
import { alignedReturns, computeQuant, correlation } from "./quantStats";
import { MARKET_API_BASE, marketFetch } from "../backendConfig";
import { palette } from "../pages/pageStyles";
import "./QuantChrome.css";

const WINDOW = 200;
const MAX_PEERS = 6;
const PEER_TTL_MS = 5 * 60 * 1000;
const peerCache = new Map(); // "SYMBOL|TF" -> { at, candles }

const pct = (v, d) => (v >= 0 ? "+" : "−") + Math.abs(v * 100).toFixed(d) + "%";
const signed = (v, d) => (v >= 0 ? "+" : "−") + Math.abs(v).toFixed(d);

function readWatchlist() {
  try {
    const list = JSON.parse(window.localStorage.getItem("lwt.watchlistSymbols") || "[]");
    return Array.isArray(list) ? list.filter((s) => typeof s === "string") : [];
  } catch {
    return [];
  }
}

async function loadPeer(symbol, tf) {
  const key = `${symbol}|${tf}`;
  const hit = peerCache.get(key);
  if (hit && Date.now() - hit.at < PEER_TTL_MS) return hit.candles;
  const res = await marketFetch(`${MARKET_API_BASE}/candles?symbol=${encodeURIComponent(symbol)}&tf=${encodeURIComponent(tf)}&limit=${WINDOW + 1}`);
  const rows = await res.json().catch(() => null);
  if (!res.ok || !Array.isArray(rows)) throw new Error(`HTTP ${res.status}`);
  peerCache.set(key, { at: Date.now(), candles: rows });
  return rows;
}

const h3 = { margin: "0 0 8px", fontSize: 12, fontWeight: 600, color: palette.mutedText };

/** Side panel next to the chart: return distribution, rolling volatility, correlations, risk. */
export default function QuantPanel({ height }) {
  const data = useChartData();
  const [watchlist, setWatchlist] = useState(readWatchlist);
  const q = useMemo(
    () => computeQuant(data.candles, { window: WINDOW, barSeconds: data.barSeconds }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data.version]
  );
  const [peers, setPeers] = useState({ key: "", rows: [], note: "" });

  useEffect(() => {
    const refreshWatchlist = () => setWatchlist(readWatchlist());
    window.addEventListener("storage", refreshWatchlist);
    window.addEventListener("lwt:watchlist-updated", refreshWatchlist);
    refreshWatchlist();
    return () => {
      window.removeEventListener("storage", refreshWatchlist);
      window.removeEventListener("lwt:watchlist-updated", refreshWatchlist);
    };
  }, []);

  // Refresh peer candles every five minutes and whenever the symbol, timeframe or watchlist changes.
  useEffect(() => {
    const symbol = (data.symbol || "").toUpperCase();
    const tf = data.timeframe;
    if (!symbol || !tf) return undefined;
    const list = watchlist.map((s) => s.toUpperCase()).filter((s) => s !== symbol).slice(0, MAX_PEERS);
    const key = `${symbol}|${tf}|${list.join(",")}`;
    if (!list.length) {
      setPeers({ key, rows: [], note: "Add symbols to the watchlist to compare them here." });
      return undefined;
    }
    let cancelled = false;
    const refresh = async () => {
      setPeers((previous) => ({
        key,
        rows: previous.key === key ? previous.rows : [],
        note: previous.key === key && previous.rows.length ? "Updating watchlist candles…" : "Loading watchlist candles…",
      }));
      const results = await Promise.all(list.map((s) => loadPeer(s, tf).then((c) => ({ s, c })).catch(() => ({ s, c: null }))));
      if (cancelled) return;
      const failed = results.filter((row) => !row.c);
      setPeers({
        key,
        rows: results,
        note: failed.length ? `Could not load ${failed.map((row) => row.s).join(", ")}. Check sign-in and market data connection.` : "",
      });
    };
    refresh();
    const interval = window.setInterval(refresh, PEER_TTL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [data.symbol, data.timeframe, watchlist]);

  const corrs = useMemo(() => {
    const mine = (data.candles || []).slice(-WINDOW - 1);
    return peers.rows.map(({ s, c }) => {
      if (!c) return { s, r: null };
      const [a, b] = alignedReturns(mine, c);
      return { s, r: a.length >= 20 ? correlation(a, b) : null };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [peers, data.version]);

  const shell = {
    flex: "0 0 300px",
    width: 300,
    height,
    minHeight: 480,
    overflowY: "auto",
    padding: "14px 16px 16px",
    display: "flex",
    flexDirection: "column",
    gap: 18,
    border: `1px solid ${palette.border}`,
    borderRadius: 6,
    background: palette.glass,
    backdropFilter: "blur(8px)",
    WebkitBackdropFilter: "blur(8px)",
    color: palette.text,
    fontSize: 12,
  };

  if (!q) {
    return (
      <aside aria-label="Quant panel" style={shell}>
        <h2 className="lwt-label" style={{ margin: 0, fontSize: 14 }}>Quant</h2>
        <p style={{ margin: 0, color: palette.mutedText }}>Needs at least 31 candles on the chart. Load more history or pick a shorter timeframe.</p>
      </aside>
    );
  }

  const { counts, lim, normal } = q.histogram;
  const cmax = Math.max(...counts, 1);
  const normalPts = normal.map((y, i) => `${((i / (normal.length - 1)) * 100).toFixed(2)},${(100 - Math.min(100, (y / cmax) * 100)).toFixed(2)}`).join(" ");
  const rv = q.rollingVol;
  const rvMin = Math.min(...rv);
  const rvMax = Math.max(...rv);
  const rvSpan = rvMax - rvMin || 1;
  const volPts = rv.map((v, i) => `${((i / Math.max(1, rv.length - 1)) * 100).toFixed(2)},${(38 - ((v - rvMin) / rvSpan) * 34).toFixed(2)}`);

  const stats = [
    ["Mean return per bar", pct(q.meanReturn, 3), q.meanReturn >= 0 ? palette.up : palette.down],
    ["Spread per bar (1 sd)", `${(q.sdReturn * 100).toFixed(3)}%`],
    ["Up bars", `${(q.upShare * 100).toFixed(1)}%`],
    ["Value at risk, 95%, one bar", pct(q.var95, 3), palette.down],
    ["Excess kurtosis", signed(q.kurtosis, 2)],
    ["Bars per year (measured)", Math.round(q.barsPerYear).toLocaleString("en-US")],
  ];

  return (
    <aside aria-label="Quant panel" style={shell}>
      <h2 className="lwt-label" style={{ margin: 0, fontSize: 14 }}>
        Quant <span style={{ color: palette.mutedText, fontWeight: 400 }}>{data.symbol} {data.timeframe}</span>
      </h2>

      <section>
        <h3 className="lwt-label" style={h3}>Return distribution, last {q.returns.length} bars</h3>
        <div style={{ position: "relative", height: 110, display: "flex", alignItems: "flex-end", gap: 2, borderBottom: `1px solid ${palette.border}` }}>
          {counts.map((c, i) => {
            const mid = -lim + (i + 0.5) * q.histogram.width;
            return (
              <div
                key={i}
                title={`${c} bars around ${pct(mid, 2)}`}
                style={{ flex: "1 1 0", height: `${(c / cmax) * 100}%`, background: mid >= 0 ? palette.up : palette.down, opacity: 0.8 }}
              />
            );
          })}
          <svg viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true" style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}>
            <polyline points={normalPts} fill="none" stroke={palette.glow} strokeWidth="1.2" strokeDasharray="3 2" vectorEffect="non-scaling-stroke" />
          </svg>
        </div>
        <div className="lwt-num" style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: palette.mutedText, marginTop: 4 }}>
          <span>{pct(-lim, 2)}</span>
          <span>0</span>
          <span>{pct(lim, 2)}</span>
        </div>
        <p style={{ margin: "6px 0 0", fontSize: 11, lineHeight: 1.5, color: palette.mutedText }}>
          Bars: actual returns. Dashed line: a normal curve with the same mean and spread. Fat edges mean bigger moves than "normal".
        </p>
      </section>

      <section>
        <h3 className="lwt-label" style={h3}>Rolling volatility, 20 bars (yearly)</h3>
        {rv.length > 1 ? (
          <>
            <svg viewBox="0 0 100 40" preserveAspectRatio="none" aria-hidden="true" style={{ width: "100%", height: 64, display: "block" }}>
              <polygon points={`0,40 ${volPts.join(" ")} 100,40`} fill="rgba(139, 124, 255, 0.12)" />
              <polyline points={volPts.join(" ")} fill="none" stroke={palette.lavender} strokeWidth="1.4" vectorEffect="non-scaling-stroke" />
            </svg>
            <div className="lwt-num" style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: palette.mutedText }}>
              <span>min {(rvMin * 100).toFixed(1)}%</span>
              <span>now {(rv[rv.length - 1] * 100).toFixed(1)}%</span>
              <span>max {(rvMax * 100).toFixed(1)}%</span>
            </div>
          </>
        ) : (
          <p style={{ margin: 0, color: palette.mutedText }}>Needs a few more candles.</p>
        )}
      </section>

      <section>
        <h3 className="lwt-label" style={h3}>Correlation with your watchlist</h3>
        {peers.note && <p style={{ margin: 0, color: palette.mutedText }}>{peers.note}</p>}
        {corrs.map(({ s, r }) => (
          <div key={s} style={{ display: "grid", gridTemplateColumns: "84px minmax(0, 1fr) 52px", alignItems: "center", gap: 8, minHeight: 26 }}>
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s}</span>
            <span style={{ position: "relative", height: 8, background: palette.field, border: `1px solid ${palette.border}` }}>
              <span style={{ position: "absolute", top: -1, bottom: -1, left: "50%", width: 1, background: palette.mutedText }} />
              {r !== null && (
                <span
                  style={{
                    position: "absolute",
                    top: 0,
                    bottom: 0,
                    left: `${r >= 0 ? 50 : 50 + r * 50}%`,
                    width: `${Math.abs(r) * 50}%`,
                    background: r >= 0 ? palette.up : palette.down,
                  }}
                />
              )}
            </span>
            <span className="lwt-num" style={{ textAlign: "right", color: r === null ? palette.mutedText : r >= 0 ? palette.up : palette.down }}>
              {r === null ? "n/a" : signed(r, 2)}
            </span>
          </div>
        ))}
      </section>

      <section>
        <h3 className="lwt-label" style={h3}>Risk and shape</h3>
        <dl style={{ margin: 0, display: "grid", gridTemplateColumns: "minmax(0, 1fr) auto", gap: "6px 12px" }}>
          {stats.map(([k, v, c]) => (
            <React.Fragment key={k}>
              <dt style={{ color: palette.mutedText }}>{k}</dt>
              <dd className="lwt-num" style={{ margin: 0, textAlign: "right", color: c || palette.text }}>{v}</dd>
            </React.Fragment>
          ))}
        </dl>
      </section>

      <p style={{ margin: 0, fontSize: 11, lineHeight: 1.5, color: palette.mutedText }}>
        From the last {q.bars} candles on the chart. Updates with live prices and replay.
      </p>
    </aside>
  );
}
